/**
 * NPC API Service
 * Handles in-world NPC (non-player character) card lookups.
 */

import { apiService } from './apiService';
import type { TalkResponse } from '../../types/memory';

// ==========================================
// TYPES
// ==========================================

export interface NpcCard {
  id: string;
  name: string;
  role: string;
  backstory: string;
  greeting: string;
}

// ==========================================
// NPC CARD
// ==========================================

/**
 * Get an NPC's card by id (without the "npc_" prefix)
 */
export async function getNpcCard(npcId: string): Promise<NpcCard | null> {
  const response = await apiService.get<NpcCard>(`/api/npcs/${npcId}/card`);
  if (response.error) {
    console.error('Failed to get NPC card:', response.error);
    return null;
  }
  return response.data || null;
}

/**
 * Say something to an NPC; returns their in-character reply.
 */
export async function talkToNpc(npcId: string, message: string): Promise<string | null> {
  const response = await apiService.post<{ reply: string }>(`/api/npcs/${npcId}/talk`, { message });
  if (response.error) {
    console.error('Failed to talk to NPC:', response.error);
    return null;
  }
  return response.data?.reply || null;
}

/**
 * Say something to an NPC and get the full response, including an optional
 * "should I remember this?" memory proposal (character-guided profiling).
 * Additive beside `talkToNpc` (L-REPLY): `memoryProposal` is omitted when no probe fires.
 */
export async function talkToNpcFull(npcId: string, message: string): Promise<TalkResponse> {
  const response = await apiService.post<TalkResponse>(`/api/npcs/${npcId}/talk`, { message });
  if (response.error) {
    console.error('Failed to talk to NPC:', response.error);
    return { reply: '' };
  }
  return {
    reply: response.data?.reply ?? '',
    memoryProposal: response.data?.memoryProposal,
  };
}

// ==========================================
// COACHING THREAD (WU-06 R6 — opt-in "wing-tip" affordance)
// ==========================================

export type CoachTopic = 'connection-coach' | 'inhibition-help';

/**
 * Ask a persona for a coaching tip. ONLY ever call this in direct response to a user
 * action (tapping the persona chip / a topic button) — the server never pushes to this
 * thread on its own, so calling it unprompted would violate R6's "never unsolicited" rule.
 */
export async function coachWithNpc(npcId: string, topic: CoachTopic, roomId?: string): Promise<string> {
  const response = await apiService.post<{ reply: string }>(`/api/npcs/${npcId}/coach`, {
    topic,
    roomId,
  });
  if (response.error) {
    console.error('Failed to get coaching reply:', response.error);
    return '';
  }
  return response.data?.reply || '';
}

// ==========================================
// EXPORT ALL
// ==========================================

export const npcService = {
  getNpcCard,
  talkToNpc,
  talkToNpcFull,
  coachWithNpc,
};
